import type { AxiosInstance } from "axios";

import { validateResponse } from "api/validateResponse";

import type {
  BroadcastMessagePayload,
  GetBroadcastMessagesArgs,
  GetConversationMessagesArgs,
  MessagePayload,
} from "./schemas/messaging";
import {
  BaseResponseSchema,
  BroadcastMessagesResponseSchema,
  ConversationMessagesResponseSchema,
  ConversationsResponseSchema,
  PostMessageResponseSchema,
} from "./schemas/messaging";
import type { DeleteQuickReplyArgs, PostQuickReplyArgs } from "./schemas/quick_replies";
import { QuickRepliesResponseSchema, QuickReplySchema } from "./schemas/quick_replies";

export const createMessagingService = (transportV1: AxiosInstance) => ({
  async deleteQuickReply({ id, userID }: DeleteQuickReplyArgs) {
    const response = await transportV1.delete(`/users/${userID}/quick_replies/${id}`);
    return validateResponse(response, QuickReplySchema, false);
  },

  async getBroadcastMessages(args: GetBroadcastMessagesArgs) {
    const params: Record<string, string | number> = {};

    if (args.limit !== undefined) {
      params["page[limit]"] = args.limit;
    }
    if (args.before !== undefined) {
      params["page[before]"] = args.before;
    }
    if (args.filters) {
      if (args.filters.all) {
        params["filters[all]"] = "true";
      } else {
        if (args.filters.ce_structure) {
          params["filters[ce_structure]"] = args.filters.ce_structure;
        }
        if (args.filters.prio) {
          params["filters[prio]"] = "true";
        }
      }
    }

    const response = await transportV1.get(`/users/${args.userID}/conversations/broadcast`, {
      params,
    });
    return validateResponse(response, BroadcastMessagesResponseSchema, false);
  },

  async getConversations(userID: number) {
    const response = await transportV1.get(`/users/${userID}/conversations`, {
      params: { digest: true },
    });
    return validateResponse(response, ConversationsResponseSchema, false);
  },

  async getConversationMessages(args: GetConversationMessagesArgs) {
    const params: Record<string, string | number> = {};

    if (args.patientID !== undefined) {
      params.peer_id = args.patientID;
    }

    if (args.limit !== undefined) {
      params["page[limit]"] = args.limit;
    }
    if (args.before !== undefined) {
      params["page[before]"] = args.before;
    }
    if (args.after !== undefined) {
      params["page[after]"] = args.after;
    }

    const response = await transportV1.get(`/users/${args.userID}/conversations`, {
      params,
    });
    return validateResponse(response, ConversationMessagesResponseSchema, false);
  },

  async getQuickReplies(userID: number) {
    const response = await transportV1.get(`/users/${userID}/quick_replies`);
    return validateResponse(response, QuickRepliesResponseSchema, false);
  },

  async postBroadcastMessage(args: BroadcastMessagePayload) {
    const response = await transportV1.post(`/messages/broadcast`, args);
    return validateResponse(response, BaseResponseSchema, false);
  },

  async postMessage(args: MessagePayload) {
    const response = await transportV1.post(`/messages`, args);
    return validateResponse(response, PostMessageResponseSchema, false);
  },

  async putMarkMessageAsRead(messageID: number) {
    const response = await transportV1.put(`/messages/${messageID}/read`);
    return validateResponse(response, BaseResponseSchema, false);
  },

  async putPinMessage(messageID: number) {
    const response = await transportV1.put(`/messages/${messageID}/pin`);
    return validateResponse(response, BaseResponseSchema, false);
  },

  async putUnpinMessage(messageID: number) {
    const response = await transportV1.put(`/messages/${messageID}/unpin`);
    return validateResponse(response, BaseResponseSchema, false);
  },

  async postQuickReply({ text, userID }: PostQuickReplyArgs) {
    const response = await transportV1.post(`/users/${userID}/quick_replies`, { text });
    return validateResponse(response, QuickReplySchema, false);
  },
});

export type MessagingService = ReturnType<typeof createMessagingService>;
